import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  CartErrorEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/**
 * @param {unknown} errors
 * @returns {string}
 */
function formatCartErrors(errors) {
  if (!errors) return '';
  if (typeof errors === 'string') return errors;
  if (Array.isArray(errors)) {
    return errors
      .map((entry) => (typeof entry === 'string' ? entry : String(entry)))
      .join(' ');
  }
  if (typeof errors === 'object') {
    return Object.values(/** @type {Record<string, unknown>} */ (errors))
      .flat()
      .map((entry) => (typeof entry === 'string' ? entry : String(entry)))
      .join(' ');
  }
  return String(errors);
}

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** Bumped per cart/change request so out-of-order responses can be discarded. */
  #requestToken = 0;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    const lineItemRow = this.refs.cartItemRows[line - 1];

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
      itemKey: lineItemRow?.dataset.key,
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      itemKey: cartItemRowToRemove?.dataset.key,
      optimistic: true,
    });

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string} [config.itemKey] - The cart line item key, preferred over the index.
   * @param {boolean} [config.optimistic] - Whether the DOM was already updated ahead of the response.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity, itemKey, optimistic = false } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // The line index shifts as soon as another row is removed; the key does not.
    const body = JSON.stringify({
      ...(itemKey ? { id: itemKey } : { line }),
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const requestToken = ++this.#requestToken;
    const isStale = () => requestToken !== this.#requestToken;

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        if (isStale()) return;

        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          try {
            this.#handleCartError(line, parsedResponseText);
          } catch (error) {
            console.error(error);
          }
          // The row was already animated away, so the error left the UI lying about the cart.
          if (optimistic) await this.#restoreFromServer();
          return;
        }

        const updatedSectionHtml = parsedResponseText.sections?.[this.sectionId];
        if (!updatedSectionHtml) {
          await sectionRenderer.renderSection(this.sectionId, { cache: false });
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(updatedSectionHtml, 'text/html');

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        const replace = this.#needsSectionReplace(updatedSectionHtml);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        if (this.sectionId === 'cart-drawer' && replace) {
          // cart-drawer-morph-fix swaps empty ↔ filled on capture during dispatchEvent.
        } else {
          await morphSection(this.sectionId, updatedSectionHtml, { replace });
        }

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch(async (error) => {
        console.error(error);
        if (isStale()) return;
        // Rows are removed ahead of the response, so a failed request must resync.
        if (optimistic) await this.#restoreFromServer();
      })
      .finally(() => {
        if (isStale()) return;
        document.querySelectorAll('cart-items-component').forEach((item) => {
          item.classList.remove('cart-items-disabled');
        });
        resetShimmer(document);
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Re-renders this section from the server after an optimistic update failed.
   */
  async #restoreFromServer() {
    try {
      await sectionRenderer.renderSection(this.sectionId, { cache: false, replace: true });
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const errorMessage =
      formatCartErrors(parsedResponseText.errors) ||
      parsedResponseText.message ||
      parsedResponseText.description ||
      'Could not update your cart. Please try again.';

    // Announce before touching the DOM so screen readers hear it even if the row is gone.
    document.dispatchEvent(
      new CartErrorEvent(this.sectionId, errorMessage, parsedResponseText.description, parsedResponseText.errors)
    );

    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = errorMessage;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event instanceof DiscountUpdateEvent) {
      // The discount response already carries rendered sections; morph instead of refetching.
      const discountHtml = event.detail.resource?.sections?.[this.sectionId];
      if (discountHtml) {
        try {
          await morphSection(this.sectionId, discountHtml, { replace: this.#needsSectionReplace(discountHtml) });
        } catch (error) {
          console.error(error);
          await sectionRenderer.renderSection(this.sectionId, { cache: false, replace: true });
        }
      } else {
        await sectionRenderer.renderSection(this.sectionId, { cache: false });
      }
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      const replace = this.#needsSectionReplace(cartItemsHtml);

      try {
        if (this.sectionId === 'cart-drawer' && replace) {
          // cart-drawer-morph-fix already replaced the drawer on capture.
        } else {
          await morphSection(this.sectionId, cartItemsHtml, { replace });
        }
      } catch (error) {
        console.error(error);
        await sectionRenderer.renderSection(this.sectionId, { cache: false, replace: true });
      }

      this.#updateCartQuantitySelectorButtonStates();
    } else {
      await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        replace: this.#isCartUiEmpty(),
      });
    }

    document.querySelectorAll('cart-items-component').forEach((item) => {
      item.classList.remove('cart-items-disabled');
    });
    resetShimmer(document);
  };

  /**
   * Whether the cart UI is currently showing the empty state.
   * @returns {boolean}
   */
  #isCartUiEmpty() {
    return Boolean(
      this.querySelector('.cart-drawer__heading--empty, .cart-items__empty') ||
        this.closest('.cart-drawer--empty, .cart-page--empty')
    );
  }

  /**
   * Empty ↔ filled cart layouts are structurally different; incremental morph cannot
   * reliably swap between them, so replace the section markup instead.
   * @param {string} cartItemsHtml
   * @returns {boolean}
   */
  #needsSectionReplace(cartItemsHtml) {
    const wasEmpty = this.#isCartUiEmpty();
    const willBeEmpty =
      cartItemsHtml.includes('cart-drawer--empty') ||
      cartItemsHtml.includes('cart-page--empty') ||
      cartItemsHtml.includes('cart-drawer__heading--empty') ||
      cartItemsHtml.includes('cart-items__empty');

    return wasEmpty !== willBeEmpty;
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
